import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import type { IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { dao } from '@platform/dao';
import type { RootState } from '../RootState';
import { baseApi } from './baseApi';
import { catalogTag } from './catalogTag';
import { metadataApi } from './metadataApi';
import { metadataBulkApi } from './metadataBulkApi';
import { moduleTag } from './moduleTag';

const replaceType = (def: IModuleDefinition, rt: IRecordTypeDefinition) => { def.recordTypes = def.recordTypes.map((r) => (r.key === rt.key ? rt : r)); };

/**
 * Кеш визначень модуля після глобальної зміни (ADR-027: опубліковане → глобальне → простір, тож змінюється кожен простір без власного
 * налаштування): глобальні визначення (без простору) отримують злитий тип одразу; визначення модуля в кожному закешованому просторі й
 * каталоги перечитуються за точними тегами; зведення визначень «Налаштувань» простору замінюють лише цей модуль (один запит, а не всі 95).
 */
export const refreshModuleCaches = (module: string, rt?: IRecordTypeDefinition): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const state = getState();
    const tags = [moduleTag(undefined, module), catalogTag(undefined), 'GlobalCatalog' as const];
    for (const arg of metadataApi.util.selectCachedArgsForQuery(state, 'getModule')) {
      if (arg.key !== module) continue;
      if (!arg.workspaceId && rt) dispatch(metadataApi.util.updateQueryData('getModule', arg, (def) => replaceType(def, rt)));
      tags.push(moduleTag(arg.workspaceId, module));
    }
    for (const arg of metadataApi.util.selectCachedArgsForQuery(state, 'getCatalog')) tags.push(catalogTag(arg.workspaceId));
    for (const arg of metadataApi.util.selectCachedArgsForQuery(state, 'getRecordType')) tags.push(moduleTag(arg.workspaceId, module));
    dispatch(baseApi.util.invalidateTags(tags));
    const bulks = metadataBulkApi.util.selectCachedArgsForQuery(state, 'getModuleDefinitions')
      .filter((arg) => (typeof arg === 'string' ? arg : arg.keys).split(',').includes(module));
    await Promise.all(bulks.map(async (arg) => {
      const ws = typeof arg === 'string' ? undefined : arg.workspaceId;
      if (!ws && rt) {
        dispatch(metadataBulkApi.util.updateQueryData('getModuleDefinitions', arg, (defs) => { defs.filter((d) => d.module.key === module).forEach((d) => replaceType(d, rt)); }));
        return;
      }
      const fresh = await dao.metadata.module(module, ws).catch(() => null);
      if (fresh) dispatch(metadataBulkApi.util.updateQueryData('getModuleDefinitions', arg, (defs) => defs.map((d) => (d.module.key === module ? fresh : d))));
    }));
  };
